
import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';
import { AdminNotification } from '@/types/adminNotifications';
import Logger from '@/utils/logger';

export const useUserNotifications = () => {
  const { user, loading: authLoading } = useAuth();
  const queryClient = useQueryClient();

  // Early return if auth is still loading to prevent incorrect "Guest user" state
  if (authLoading) {
    Logger.debug('useUserNotifications called - auth still loading');
    return {
      notifications: [],
      isLoading: true,
      dismissNotification: () => {},
      isDismissing: false,
    };
  }

  Logger.debug('useUserNotifications called');
  Logger.debug('User in hook:', user?.id || 'Guest user');
  Logger.debug('Auth loading state:', authLoading);

  // Fetch active notifications for all users (including guests)
  const { data: notifications = [], isLoading, error } = useQuery({
    queryKey: ['user-notifications', user?.id || 'guest'],
    enabled: !authLoading, // Don't fetch until auth is resolved
    queryFn: async () => {
      Logger.debug('Starting notifications fetch...');
      Logger.info('Fetching from database...');
      Logger.info('Current user:', user?.id || 'Guest');
      
      const { data, error } = await supabase
        .from('admin_notifications')
        .select(`
          *,
          user_notification_dismissals!left(
            id,
            dismissed_at
          )
        `)
        .eq('is_active', true)
        .or('start_date.is.null,start_date.lte.' + new Date().toISOString())
        .or('end_date.is.null,end_date.gt.' + new Date().toISOString())
        .order('priority', { ascending: false })
        .order('created_at', { ascending: false });
      
      Logger.debug('Query completed');
      Logger.debug('Data received:', data);
      Logger.debug('Error:', error);
      
      Logger.info('Raw query result:');
      Logger.info('  - Data:', data);
      Logger.info('  - Error:', error);
      Logger.info('  - Data count:', data?.length || 0);
      Logger.info('  - User ID for filtering:', user?.id || 'Guest');
      
      if (error) {
        Logger.error('Query error:', error);
        throw error;
      }

      // Filter out dismissed notifications (only for logged-in users)
      const filteredNotifications = data?.filter(notification => {
        // For guest users, show all non-dismissible notifications or all if no dismissal system
        if (!user) {
          Logger.info(`Guest user - showing notification "${notification.title}"`);
          return true;
        }

        // For logged-in users, check dismissals
        const isDismissed = notification.is_dismissible && 
          notification.user_notification_dismissals?.some(
            (dismissal: any) => dismissal.id && !dismissal.dismissed_at
          );
        
        Logger.info(`Notification "${notification.title}":`, {
          id: notification.id,
          isDismissed,
          isDismissible: notification.is_dismissible,
          dismissals: notification.user_notification_dismissals
        });
        
        return !isDismissed;
      }) || [];

      Logger.debug('Filtered notifications:', filteredNotifications);
      Logger.info('Filtered notifications:', filteredNotifications);
      Logger.info('Final count:', filteredNotifications.length);

      return filteredNotifications as AdminNotification[];
    },
  });

  Logger.debug('Query state:', { isLoading, error, notificationsCount: notifications?.length });

  Logger.info('Hook result:', {
    notificationsCount: notifications?.length || 0,
    isLoading,
    notifications: notifications?.map(n => ({ id: n.id, title: n.title, type: n.type }))
  });

  // Dismiss notification mutation (only works for logged-in users)
  const dismissMutation = useMutation({
    mutationFn: async (notificationId: string) => {
      if (!user) {
        Logger.info('Guest user cannot dismiss notifications');
        return;
      }

      Logger.info('Dismissing notification:', notificationId);

      // Record dismissal
      const { error } = await supabase
        .from('user_notification_dismissals')
        .insert([{
          user_id: user.id,
          notification_id: notificationId,
        }]);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-notifications'] });
    },
  });

  // Real-time subscription (works for all users)
  useEffect(() => {
    Logger.info('Setting up real-time subscription...');

    const channel = supabase
      .channel('user-notifications-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'admin_notifications'
        },
        (payload) => {
          Logger.info('Real-time update:', payload);
          queryClient.invalidateQueries({ queryKey: ['user-notifications'] });
        }
      )
      .subscribe();

    return () => {
      Logger.info('Cleaning up real-time subscription...');
      supabase.removeChannel(channel);
    };
  }, [queryClient]);

  return {
    notifications,
    isLoading,
    dismissNotification: dismissMutation.mutate,
    isDismissing: dismissMutation.isPending,
  };
};
